"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


const getQueryParams = () => {
  if (typeof window !== "undefined" && (window as any).wvcClient) {
    return (window as any).wvcClient.getCurrentWPQueryParams();
  }
  return { post_type: "post" };
};

function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-[400px] rounded-xl bg-muted/30 animate-pulse" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} className="group flex flex-col h-full border-border/50 bg-card hover:shadow-lg transition-all duration-300 overflow-hidden" data-index={i}>
          <div className="relative h-48 overflow-hidden">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground">
                <span className="text-sm">No Image</span>
              </div>
            )}
            <div className="absolute top-4 left-4">
              <span className="bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                {post.categories?.[0]?.name || "News"}
              </span>
            </div>
          </div>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Calendar className="size-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recent"}</span>
            </div>
            <CardTitle className="font-serif text-xl leading-tight group-hover:text-primary transition-colors line-clamp-2">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow">
            <div 
              className="text-muted-foreground text-sm line-clamp-3"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between group-hover:bg-primary/5 group-hover:text-primary">
                Read Article <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPost }: { currentPost: Post }) {
  const categoryId = currentPost.categories?.[0]?.id;
  
  
  const queryArgs = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [currentPost.id],
    ...(categoryId ? { category__in: [categoryId] } : {}),
    orderby: "date",
    order: "DESC"
  };

  const wp_query = new WP_Query(queryArgs);

  return (
    <div className="mt-24 border-t border-border pt-16">
      <div className="flex items-center justify-between mb-10">
        <h3 className="font-serif text-3xl font-bold text-primary">Related News</h3>
        <Link to="/news">
          <Button variant="outline" className="hidden md:flex">View All News</Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsList />
      </PostsProvider>
      <div className="mt-8 md:hidden">
        <Link to="/news" className="w-full">
          <Button variant="outline" className="w-full">View All News</Button>
        </Link>
      </div>
    </div>
  );
}

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8 animate-pulse">
        <div className="h-8 bg-muted/30 w-3/4 rounded" />
        <div className="h-[400px] bg-muted/30 w-full rounded-xl" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/30 w-full rounded" />
          <div className="h-4 bg-muted/30 w-full rounded" />
          <div className="h-4 bg-muted/30 w-2/3 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-destructive mb-4">Error Loading Post</h2>
        <p className="text-muted-foreground mb-6">We couldn't load the requested article. Please try again.</p>
        <Link to="/">
          <Button>Return Home</Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-primary mb-4">Post Not Found</h2>
        <p className="text-muted-foreground mb-6">The article you are looking for does not exist or has been moved.</p>
        <Link to="/">
          <Button>Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      {}
      <header className="max-w-4xl mx-auto text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-6">
          {post.categories?.map((cat, i) => (
            <span key={i} className="bg-secondary/10 text-secondary-foreground text-sm font-semibold px-4 py-1.5 rounded-full uppercase tracking-wide border border-secondary/20">
              {cat.name}
            </span>
          ))}
        </div>
        
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight mb-8">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground text-sm md:text-base">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8 border border-border">
                <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
                <AvatarFallback><User className="size-4" /></AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <span>{post.date ? new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ""}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="size-4" />
            <span>5 min read</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-16 relative rounded-2xl overflow-hidden shadow-2xl aspect-video md:aspect-[21/9]">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {}
        <div className="lg:col-span-8 lg:col-start-3">
          <div 
            className="prose prose-lg prose-slate max-w-none 
              prose-headings:font-serif prose-headings:text-primary prose-headings:font-bold 
              prose-p:text-muted-foreground prose-p:leading-relaxed
              prose-a:text-secondary prose-a:no-underline hover:prose-a:underline
              prose-img:rounded-xl prose-img:shadow-lg
              prose-blockquote:border-l-4 prose-blockquote:border-secondary prose-blockquote:bg-muted/20 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
              prose-li:marker:text-secondary"
            dangerouslySetInnerHTML={{ __html: post.content || "" }}
          />

          {}
          <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex flex-wrap gap-2">
              {post.tags?.map((tag, i) => (
                <div key={i} className="flex items-center gap-1.5 text-sm text-muted-foreground bg-muted px-3 py-1.5 rounded-md hover:bg-muted/80 transition-colors cursor-default">
                  <Tag className="size-3.5" />
                  {tag.name}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-primary">Share:</span>
              <Button variant="outline" size="icon" className="rounded-full size-9 hover:text-primary hover:border-primary">
                <Share2 className="size-4" />
              </Button>
              {}
            </div>
          </div>

          {}
          {post.author && (
            <div className="mt-12 bg-muted/30 border border-border rounded-xl p-8 flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left">
              <Avatar className="size-20 md:size-24 border-2 border-background shadow-sm">
                <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
                <AvatarFallback className="text-2xl bg-secondary/20 text-secondary-foreground">
                  {post.author.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="text-xl font-serif font-bold text-primary mb-2">About {post.author.name}</h3>
                <p className="text-muted-foreground mb-4">
                  {post.author.description || `Contributor at Colorado Hispanic Chamber of Commerce. Passionate about economic development and community empowerment.`}
                </p>
                <Link to={post.author.link || "#"}>
                  <Button variant="link" className="p-0 h-auto text-secondary font-semibold">
                    View all posts by {post.author.name}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      <RelatedPostsWrapper currentPost={post} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = getQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}